import React, { useState } from 'react';
import { AlertCircle, Plus } from 'lucide-react';
import { StandardAppLayout } from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import { Button } from 'src/shared/components/ui/button';
import Alert from 'src/shared/components/ui/alert';
import { useCustomersQuery } from 'src/app/customers/state/api/customersGraphSlice';
import AddOrganisationSheet from 'src/app/admin/components/add-organisation-sheet/AddOrganisationSheet';
import OrgManagementTable from './components/org-management-table/OrgManagementTable';

const OrgManagement = () => {
    const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);
    const [information, setInformation] = useState<string | null>(null);
    const { data, isLoading, error, refetch } = useCustomersQuery({});

    const organisations = data?.customers ?? [];

    const openAddSheet = () => {
        setInformation(null);
        setIsAddSheetOpen(true);
    };

    const mainContent = (() => {
        if (isLoading) {
            return (
                <div className='flex justify-center py-24'>
                    <Loader />
                </div>
            );
        }

        if (error) {
            return (
                <div className='flex flex-col items-center justify-center py-24 gap-4 text-center' data-testid='org-management-error'>
                    <div className='flex items-center justify-center w-16 h-16 rounded-full bg-red-0'>
                        <AlertCircle className='w-8 h-8 text-red-100' />
                    </div>
                    <div className='flex flex-col items-center gap-1 max-w-sm'>
                        <h3 className='text-lg font-semibold text-primary'>Unable to load organisations</h3>
                        <p className='text-sm text-grey-70'>Something went wrong fetching the organisation list. Try again.</p>
                    </div>
                    <Button variant='outline' onClick={() => refetch()}>
                        Retry
                    </Button>
                </div>
            );
        }

        return (
            <div className='pb-6 flex flex-col gap-4'>
                {information && (
                    <Alert severity='info' role='status'>
                        {information}
                    </Alert>
                )}
                <OrgManagementTable organisations={organisations} />
            </div>
        );
    })();

    return (
        <>
            <StandardAppLayout
                title='Organisation Management'
                subTitle='Search for an organisation, or create a new one'
                stickyHeader={false}
                otherActions={
                    <Button onClick={openAddSheet} data-testid='org-management-add'>
                        <Plus />
                        Add organisation
                    </Button>
                }
                mainContent={mainContent}
            />
            <AddOrganisationSheet open={isAddSheetOpen} onOpenChange={setIsAddSheetOpen} onInformation={setInformation} />
        </>
    );
};

export default OrgManagement;
